// src/pages/Convocations/ConvocationsAdminPage.jsx
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import { FaCalendarAlt, FaFilter, FaUsers } from "react-icons/fa";

import { getAllEvents } from "./../../services/eventServices";
import { useUser } from "./../../contexts/UserContext";
import { getRoleFromRoleId } from "./../../utils/roleHelper";
import useScrollToTop from "./../../hooks/useScrollToTop";
import ScrollIndicator from "./../../hooks/ScrollIndicator";
import Button from "./../../components/Button/Button";
import ActorSelectionModal from "./ActorSelectionModal";
import ActorUnregistrationModal from "./ActorUnregistrationModal";

const ConvocationsAdminPage = () => {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);
  const navigate = useNavigate();

  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [selectedType, setSelectedType] = useState("Tous");
  const [selectedDateFilter, setSelectedDateFilter] = useState("Tous");
  const [isActorSelectionModalOpen, setIsActorSelectionModalOpen] = useState(false);
  const [isActorUnregistrationModalOpen, setIsActorUnregistrationModalOpen] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState(null);

  /* ===== Helpers UI ===== */

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleDateString("fr-FR", {
      weekday: "short",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getEventStatus = (start) => {
    const now = new Date();
    const d = new Date(start);
    if (Number.isNaN(d.getTime())) {
      return {
        label: "Date inconnue",
        className: "bg-slate-100 text-slate-700 ring-slate-200",
      };
    }
    if (d < now) {
      return {
        label: "Passé",
        className: "bg-slate-100 text-slate-700 ring-slate-200",
      };
    }
    const isToday =
      d.toDateString() === now.toDateString();

    if (isToday) {
      return {
        label: "Aujourd’hui",
        className: "bg-amber-100 text-amber-800 ring-amber-200",
      };
    }
    return {
      label: "À venir",
      className: "bg-emerald-100 text-emerald-800 ring-emerald-200",
    };
  };

  const getTypeBadgeClass = (type) => {
    const t = (type || "").toLowerCase();
    if (t.includes("label") || t.includes("excellence")) {
      return "bg-purple-100 text-purple-800 ring-purple-200";
    }
    if (t.includes("inter") || t.includes("ligue")) {
      return "bg-blue-100 text-blue-800 ring-blue-200";
    }
    if (t.includes("depart") || t.includes("département")) {
      return "bg-emerald-100 text-emerald-800 ring-emerald-200";
    }
    if (t.includes("stage") || t.includes("formation")) {
      return "bg-amber-100 text-amber-800 ring-amber-200";
    }
    return "bg-slate-100 text-slate-800 ring-slate-200";
  };

  /* ===== Fetch events ===== */

  const fetchEvents = async () => {
    try {
      const data = await getAllEvents();
      setEvents(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur lors du chargement des événements", error);
      toast.error("Erreur lors du chargement des événements.");
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  /* ===== Filtres ===== */

  const applyFilters = () => {
    let filtered = [...events];

    // Filtre texte
    if (searchText) {
      const term = searchText.toLowerCase();
      filtered = filtered.filter((event) => {
        const titre = (event.titre || "").toLowerCase();
        const desc = (event.description || "").toLowerCase();
        return titre.includes(term) || desc.includes(term);
      });
    }

    // Filtre type
    if (selectedType !== "Tous") {
      filtered = filtered.filter((event) => event.type_event === selectedType);
    }

    // Filtre date
    const now = new Date();
    if (selectedDateFilter === "Passés") {
      filtered = filtered.filter((event) => new Date(event.start) < now);
    } else if (selectedDateFilter === "Prochains") {
      filtered = filtered.filter((event) => new Date(event.start) >= now);
    }

    // Tri chronologique
    filtered.sort(
      (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
    );

    setFilteredEvents(filtered);
  };

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, searchText, selectedType, selectedDateFilter]);

  const typeOptions = useMemo(
    () =>
      Array.from(
        new Set(
          events
            .map((e) => e.type_event)
            .filter((t) => t && t.trim() !== "")
        )
      ).sort(),
    [events]
  );

  /* ===== Stats ===== */
  const stats = useMemo(() => {
    const total = filteredEvents.length;
    const now = new Date();
    const aVenir = filteredEvents.filter(
      (e) => new Date(e.start) >= now
    ).length;
    const passes = total - aVenir;
    return { total, aVenir, passes };
  }, [filteredEvents]);

  /* ===== Modales ===== */

  const openActorSelectionModal = (eventId) => {
    setSelectedEventId(eventId);
    setIsActorSelectionModalOpen(true);
  };

  const closeActorSelectionModal = () => {
    setIsActorSelectionModalOpen(false);
  };

  const openActorUnregistrationModal = (eventId) => {
    setSelectedEventId(eventId);
    setIsActorUnregistrationModalOpen(true);
  };

  const closeActorUnregistrationModal = () => {
    setIsActorUnregistrationModalOpen(false);
  };

  const goToEventPage = (eventId) => {
    navigate(`/convocations-admin/${eventId}`);
  };

  const currentEvent =
    events.find((e) => e.id === selectedEventId) || null;

  return (
    <div className="flex flex-col w-full mx-auto p-2 mt-[130px] sm:mt-[130px] md:mt-[150px] lg:mt-[150px] xl:mt-[150px] dark:text-white">
      <ScrollIndicator />

      {/* TITRES */}
      <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-light tracking-[0.4em] text-gray-800 text-center dark:text-white mb-4">
        ADMINISTRATEURS
      </h2>
      <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold tracking-widest text-gray-800 text-center dark:text-white mb-6 font-montserrat uppercase">
        Convocations des Acteurs
      </h2>

      {/* BLOC FILTRES + STATS */}
      <div className="w-full max-w-[1280px] mx-auto mb-5 rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/70 backdrop-blur">
        {/* Bandeau haut */}
        <div className="bg-gradient-to-r from-blue-500 via-indigo-500 to-sky-500 px-4 sm:px-6 py-4 text-white">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <p className="text-xs sm:text-sm uppercase tracking-[0.3em] opacity-80">
                PLANIFICATION ARBITRAGE & BÉNÉVOLES
              </p>
              <p className="text-sm sm:text-base mt-1 flex items-center gap-2">
                <FaCalendarAlt />
                <span>
                  Choisissez un événement, convoquez les acteurs et
                  suivez la composition de vos équipes.
                </span>
              </p>
            </div>

            {/* Stats */}
            <div className="flex flex-wrap justify-start sm:justify-end gap-2 text-[11px] sm:text-xs">
              <div className="inline-flex items-center gap-2 rounded-xl bg-white/95 text-slate-900 px-3 py-1 ring-1 ring-slate-200">
                <span className="h-2.5 w-2.5 rounded-full bg-slate-700" />
                <span className="font-semibold">Événements</span>
                <span className="text-slate-600">
                  ({stats.total})
                </span>
              </div>
              <div className="inline-flex items-center gap-2 rounded-xl bg-white/95 text-slate-900 px-3 py-1 ring-1 ring-emerald-200">
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
                <span>À venir</span>
                <span className="text-slate-600">
                  ({stats.aVenir})
                </span>
              </div>
              <div className="inline-flex items-center gap-2 rounded-xl bg-white/95 text-slate-900 px-3 py-1 ring-1 ring-slate-200">
                <span className="h-2.5 w-2.5 rounded-full bg-slate-400" />
                <span>Passés</span>
                <span className="text-slate-600">
                  ({stats.passes})
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Filtres */}
        <div className="px-4 sm:px-6 py-4">
          <div className="flex flex-col md:flex-row gap-3 md:items-end">
            {/* Recherche */}
            <div className="flex-1">
              <label className="block text-xs font-semibold mb-1 uppercase tracking-wide text-slate-600 dark:text-slate-300">
                Recherche (titre, description…)
              </label>
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">
                  <FaFilter />
                </span>
                <input
                  type="text"
                  placeholder="Rechercher un événement…"
                  className="input input-bordered w-full pl-8 text-sm dark:bg-slate-800 dark:border-slate-700 dark:text-white"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </div>
            </div>

            {/* Type */}
            <div className="w-full md:w-1/3">
              <label className="block text-xs font-semibold mb-1 uppercase tracking-wide text-slate-600 dark:text-slate-300">
                Type d’événement
              </label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="select select-bordered w-full text-sm dark:bg-slate-800 dark:border-slate-700 dark:text-white"
              >
                <option value="Tous">Tous les types</option>
                {typeOptions.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            {/* Date */}
            <div className="w-full md:w-1/3">
              <label className="block text-xs font-semibold mb-1 uppercase tracking-wide text-slate-600 dark:text-slate-300">
                Période
              </label>
              <select
                value={selectedDateFilter}
                onChange={(e) => setSelectedDateFilter(e.target.value)}
                className="select select-bordered w-full text-sm dark:bg-slate-800 dark:border-slate-700 dark:text-white"
              >
                <option value="Tous">Tous les événements</option>
                <option value="Passés">Événements passés</option>
                <option value="Prochains">Événements à venir</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* TABLEAU DES ÉVÉNEMENTS */}
      <div className="bg-gray-100 dark:bg-gray-500/60 shadow-lg w-full max-w-[1280px] mx-auto font-montserrat mt-2 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table-auto bg-white dark:bg-slate-300 font-montserrat w-full border-collapse border-2 border-slate-200 dark:border-slate-600">
            <thead className="bg-gray-800 text-white">
              <tr>
                <th className="px-3 py-3 border-b-2 border-gray-200 text-left text-[11px] font-semibold uppercase tracking-wider">
                  Date
                </th>
                <th className="px-3 py-3 border-b-2 border-gray-200 text-left text-[11px] font-semibold uppercase tracking-wider">
                  Événement
                </th>
                <th className="px-3 py-3 border-b-2 border-gray-200 text-left text-[11px] font-semibold uppercase tracking-wider">
                  Type
                </th>
                <th className="px-3 py-3 border-b-2 border-gray-200 text-center text-[11px] font-semibold uppercase tracking-wider">
                  Convocations
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredEvents.length > 0 ? (
                filteredEvents.map((event) => {
                  const status = getEventStatus(event.start);
                  return (
                    <tr
                      key={event.id}
                      className="hover:bg-slate-50 dark:hover:bg-slate-200/60 hover:dark:text-slate-900 text-sm transition-colors"
                    >
                      {/* Date + statut */}
                      <td className="px-3 py-3 align-top">
                        <div className="text-xs font-semibold text-slate-800">
                          {formatDate(event.start)}
                        </div>
                        <span
                          className={
                            "inline-flex mt-1 items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ring-1 " +
                            status.className
                          }
                        >
                          {status.label}
                        </span>
                      </td>

                      {/* Titre + petit extrait */}
                      <td className="px-3 py-3 align-top">
                        <div className="font-semibold text-sm text-slate-900">
                          {event.titre}
                        </div>
                        {event.description && (
                          <div className="text-[11px] text-slate-500 mt-1 line-clamp-2">
                            {event.description}
                          </div>
                        )}
                      </td>

                      {/* Type */}
                      <td className="px-3 py-3 align-top">
                        {event.type_event ? (
                          <span
                            className={
                              "inline-flex items-center px-2.5 py-1 rounded-md text-[11px] font-semibold ring-1 " +
                              getTypeBadgeClass(event.type_event)
                            }
                          >
                            {event.type_event}
                          </span>
                        ) : (
                          <span className="text-[11px] text-slate-400">
                            Non renseigné
                          </span>
                        )}
                      </td>

                      {/* Actions convocation */}
                      <td className="px-3 py-3 align-top">
                        <div className="flex flex-col sm:flex-row gap-2 justify-center sm:justify-end">
                          <Button
                            text="Inscription"
                            onClick={() => openActorSelectionModal(event.id)}
                            hoverLabel="Convoquer un acteur sur cet événement"
                            className="btn-primary btn-sm gap-1"
                          />
                          <Button
                            text="Désinscription"
                            onClick={() =>
                              openActorUnregistrationModal(event.id)
                            }
                            hoverLabel="Gérer les désinscriptions"
                            className="btn-warning btn-sm gap-1"
                          />
                          <Button
                            text="Inscrits"
                            onClick={() => goToEventPage(event.id)}
                            hoverLabel="Voir la liste des acteurs convoqués"
                            className="btn-ghost btn-sm gap-1"
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={4}
                    className="text-center py-6 px-6 text-gray-700 text-sm"
                  >
                    Aucun événement ne correspond aux filtres.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ActorSelectionModal
        isOpen={isActorSelectionModalOpen}
        onClose={closeActorSelectionModal}
        eventId={selectedEventId}
        eventTitle={currentEvent?.titre}
        eventStart={currentEvent?.start}
        refreshList={fetchEvents}
      />

      <ActorUnregistrationModal
        isOpen={isActorUnregistrationModalOpen}
        onClose={closeActorUnregistrationModal}
        eventId={selectedEventId}
        eventTitle={currentEvent?.titre}
        eventStart={currentEvent?.start}
        refreshList={fetchEvents}
      />

      <ToastContainer />
    </div>
  );
};

export default ConvocationsAdminPage;
